/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';

import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

export const InventoryTxTypes = Object.freeze({
  PRODUCED: 'produced',
  SHIPPED: 'shipped',
  ADJUST: 'adjust',
});

export const inventoryTxSchema = Yup.object().shape({
  productDocId: Yup.string().required('productDocId is required'),
  productVersionId: Yup.string().required('productVersionId is required'),
  sku: Yup.string().required('sku is required'),
  type: Yup.string().oneOf(Object.values(InventoryTxTypes)).required('Inventory transaction type is required'),
  deltaOnHand: Yup.number().integer().required('deltaOnHand is required'),
  onHandAfter: Yup.number().integer().min(0).nullable()
    .default(null),
  travelerIds: Yup.array().of(Yup.string()).default([]),
  lineItemId: Yup.string().nullable(),
  shipmentId: Yup.string().nullable(),
  reason: Yup.string().nullable(),
  createdAt: YupFirestoreTimestamp.nullable(),
  createdBy: Yup.object().shape({
    uid: Yup.string().required('createdBy.uid is required'),
    email: Yup.string().email().required('createdBy.email is required'),
  }).required('createdBy is required'),
});

/**
 * @typedef {Yup.InferType<typeof inventoryTxSchema>} InventoryTxSchema
 * @typedef {BaseDocData & InventoryTxSchema} InventoryTxDocData
 */

export default class InventoryTx {
  /** @type {InventoryTxDocData['validation']} */ validation = 'passed';

  constructor({
    id,
    productDocId,
    productVersionId,
    sku,
    type,
    deltaOnHand,
    onHandAfter = null,
    travelerIds = [],
    lineItemId = null,
    shipmentId = null,
    reason = null,
    createdAt,
    createdBy,
  }) {
    const validatedData = inventoryTxSchema.validateSync({
      productDocId,
      productVersionId,
      sku,
      type,
      deltaOnHand,
      onHandAfter,
      travelerIds,
      lineItemId,
      shipmentId,
      reason,
      createdAt,
      createdBy,
    });

    this.id = id || null;
    Object.assign(this, validatedData);
  }

  toFirestore({ serverTimestamp }) {
    return {
      productDocId: this.productDocId,
      productVersionId: this.productVersionId,
      sku: this.sku,
      type: this.type,
      deltaOnHand: this.deltaOnHand,
      onHandAfter: this.onHandAfter,
      travelerIds: this.travelerIds,
      lineItemId: this.lineItemId || null,
      shipmentId: this.shipmentId || null,
      reason: this.reason || null,
      createdAt: this.createdAt || serverTimestamp(),
      createdBy: this.createdBy,
    };
  }

  static fromFirestore(docData) {
    return new InventoryTx(docData);
  }
}
